import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../lib/utils"
import { Icon } from "./icon"

const ctaButtonVariants = cva(
  "inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-t-md px-4 text-body-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-bg-white disabled:bg-disabled",
        secondary: "bg-secondary text-bg-white disabled:bg-disabled",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface CTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof ctaButtonVariants> {
  asChild?: boolean
  subtext?: string
  icon?: React.ReactNode
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, variant, asChild = false, children, subtext, icon, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(ctaButtonVariants({ variant, className }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {icon ?? (
          <Icon
            name="confirmation-number"
            className={cn("size-6 shrink-0 [&_path]:fill-current", disabled && "text-bg-white/60")}
          />
        )}
        <span className={cn("flex-1 text-left", disabled && "text-bg-white/60")}>{children}</span>
        {subtext && (
          <span
            className={cn("text-body-lg font-semibold", disabled ? "text-text" : "text-bg-white")}
          >
            {subtext}
          </span>
        )}
      </Comp>
    )
  }
)
CTAButton.displayName = "CTAButton"

export { CTAButton, ctaButtonVariants }
